/* eslint-disable complexity */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend, { type HttpBackendOptions } from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import capitalizePostProcessor from '@/i18n/capitalize-postprocessor';
import enTranslations from './locales/en/translation.json';

interface SetupI18nOptions {
  tenancy: string;
  token?: string;
  providedResources?: Record<string, any>;
  defaultLanguage?: string;
  skipBackend?: boolean;
}

/**
 * Loads a local translation file and merges it into the i18n resources.
 * Can be configured to either overwrite existing keys or only add new ones.
 * @param lang The language (e.g., 'en') to load.
 * @param overwrite If true, keys from this file will overwrite existing ones.
 *                  If false, it will only add keys that don't already exist.
 */
async function loadAndMergeLocalFile(lang: string, overwrite: boolean): Promise<void> {
  try {
    const response = await fetch(`/locales/${lang}/translation.json`);
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    const localTranslations = await response.json();
    i18n.addResourceBundle(lang, 'translation', localTranslations, true, overwrite);
    console.info(`[i18n] Loaded local translations for "${lang}" (overwrite: ${overwrite})`);
  } catch (error) {
    console.warn(`Local file for "${lang}" not found or failed to load, skipping:`, error);
    // If we are in a mode that requires this file, re-throw the error.
    if (overwrite) {
      throw error;
    }
  }
}

export async function setupI18n({
  tenancy,
  token,
  providedResources = {},
  defaultLanguage = 'en',
  skipBackend = false
}: SetupI18nOptions): Promise<{ success: boolean; language: string; resources: Record<string, any> }> {
  const i18nInstance = i18n.use(initReactI18next).use(LanguageDetector);

  // If we aren't skipping the backend, configure and use it.
  if (!skipBackend) {
    i18nInstance.use(HttpBackend);
  }

  const initOptions: Parameters<typeof i18n.init>[0] = {
    fallbackLng: defaultLanguage,
    debug: false, // turn back on to see missing keys in the console
    // This is the key to stripping region codes, e.g., 'en-GB' -> 'en'
    load: 'languageOnly',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: []
    },
    postProcess: ['capitalize']
  };

  if (!skipBackend) {
    const { global } = await import('./global');
    initOptions.backend = {
      loadPath: `${global.config.api}/meta/lang/{{lng}}`,
      requestOptions: {
        headers: {
          'x-tenancy': tenancy,
          ...(token && { Authorization: `Bearer ${token}` })
        },
        mode: 'cors'
      }
    } as HttpBackendOptions;
  }

  try {
    // Add the post-processor before initialization
    i18n.use(capitalizePostProcessor);
    await i18nInstance.init(initOptions);

    // After init, i18n.language might be something like 'en-US'.
    // We want to strip the region code to get the base language (e.g., 'en').
    const detectedLangWithRegion = i18n.language;
    const currentLang = detectedLangWithRegion.split('-')[0];

    // If the base language is different, explicitly set i18next to use it.
    if (currentLang !== detectedLangWithRegion) {
      await i18n.changeLanguage(currentLang);
    }

    // Manually provided resources are loaded first and overwrite everything.
    const manualLangData = providedResources[currentLang];
    if (manualLangData) {
      i18n.addResourceBundle(currentLang, 'translation', manualLangData, true, true);
    }

    if (skipBackend) {
      // Backend is skipped, so local files are the only source. Overwrite anything.
      try {
        await loadAndMergeLocalFile(currentLang, true);
      } catch (e) {
        console.error(`Failed to load required local translations for "${currentLang}"`, e);
        if (currentLang !== defaultLanguage) {
          // Fallback to loading the default language file
          await loadAndMergeLocalFile(defaultLanguage, true);
          await i18n.changeLanguage(defaultLanguage);
        } else {
          throw new Error('Could not load any local translation files.');
        }
      }
    } else {
      // Backend is primary. Load local files as key-level fallbacks (no overwrite).
      // We load for both the current and default languages to enrich the entire fallback chain.
      await loadAndMergeLocalFile(currentLang, false);
      if (currentLang !== defaultLanguage) {
        await loadAndMergeLocalFile(defaultLanguage, false);
      }
    }

    const finalLang = i18n.language;
    const loadedResources = i18n.getResourceBundle(finalLang, 'translation') || {};

    console.info(
      `[i18n] initialized. Mode: ${
        skipBackend ? 'local-only' : 'remote-with-key-fallback'
      }. Language: ${finalLang}`
    );

    return { success: true, language: finalLang, resources: loadedResources };
  } catch (error) {
    console.error('Error initializing i18n:', error);
    return { success: false, language: defaultLanguage, resources: {} };
  }
}

if (import.meta.env.MODE === 'test' && !i18n.isInitialized) {
  i18n
    .use(capitalizePostProcessor)
    .use(initReactI18next)
    .init({
      lng: 'en',
      fallbackLng: 'en',
      resources: {
        en: {
          translation: enTranslations,
        },
      },
      interpolation: {
        escapeValue: false,
      },
      postProcess: ['capitalize'],
    });
}

export default i18n;
